import React, { useEffect, useMemo, useRef } from 'react';

const normalizeText = (value) => String(value ?? '');

export const InlineEditableText = ({
  as = 'span',
  value,
  editable = false,
  multiline = false,
  style,
  className,
  onChange,
  placeholder = '',
}) => {
  const textRef = useRef(null);
  const normalizedValue = useMemo(() => normalizeText(value), [value]);

  useEffect(() => {
    if (!editable || !textRef.current) {
      return;
    }

    if (textRef.current.innerText !== normalizedValue) {
      textRef.current.innerText = normalizedValue;
    }
  }, [editable, normalizedValue]);

  const handleInput = (event) => {
    if (!editable || !onChange) {
      return;
    }

    onChange(event.currentTarget.innerText);
  };

  const handleKeyDown = (event) => {
    if (multiline || event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    event.currentTarget.blur();
  };

  const sharedProps = {
    className,
    style,
  };

  if (!editable) {
    return React.createElement(as, sharedProps, normalizedValue || placeholder);
  }

  return React.createElement(as, {
    ...sharedProps,
    ref: textRef,
    contentEditable: true,
    suppressContentEditableWarning: true,
    onInput: handleInput,
    onKeyDown: handleKeyDown,
    onMouseDown: (event) => event.stopPropagation(),
    onClick: (event) => event.stopPropagation(),
    spellCheck: false,
    role: 'textbox',
    'aria-multiline': multiline,
    'data-inline-editable': 'true',
    'data-placeholder': placeholder,
    style: {
      ...style,
      outline: 'none',
      boxShadow: 'inset 0 0 0 1px rgba(224,112,0,0.35)',
      borderRadius: '4px',
      padding: '1px 2px',
      cursor: 'text',
      whiteSpace: multiline ? 'pre-line' : style?.whiteSpace,
    },
  }, normalizedValue || placeholder);
};
